import { useState, useEffect } from 'react';
import { Search, Filter } from 'lucide-react';
import { fetchProvinces, fetchDistricts, fetchConstituencies } from '@/lib/api';
import type { Province, District, Constituency } from '@/lib/types';

interface SidebarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedProvince: string | null;
  onProvinceChange: (provinceId: string | null) => void;
  selectedDistrict: string | null;
  onDistrictChange: (districtId: string | null) => void;
  onConstituencySelect: (constituency: Constituency) => void;
}

export function Sidebar({
  searchQuery,
  onSearchChange,
  selectedProvince,
  onProvinceChange,
  selectedDistrict,
  onDistrictChange,
  onConstituencySelect,
}: SidebarProps) {
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [constituencies, setConstituencies] = useState<Constituency[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchProvinces().then(setProvinces).catch(console.error);
  }, []);

  useEffect(() => {
    if (selectedProvince) {
      fetchDistricts(selectedProvince).then(setDistricts).catch(console.error);
    } else {
      setDistricts([]);
      onDistrictChange(null);
    }
  }, [selectedProvince]);

  useEffect(() => {
    setIsLoading(true);
    fetchConstituencies({
      provinceId: selectedProvince || undefined,
      districtId: selectedDistrict || undefined,
      search: searchQuery || undefined,
    })
      .then(setConstituencies)
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, [selectedProvince, selectedDistrict, searchQuery]);

  return (
    <aside className="w-80 bg-white border-r border-border flex flex-col">
      <div className="p-4 border-b border-border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search constituencies..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </div>

      <div className="p-4 border-b border-border space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Filter className="w-4 h-4" />
          <span>Filters</span>
        </div>

        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">
            Province
          </label>
          <select
            value={selectedProvince || ''}
            onChange={(e) => onProvinceChange(e.target.value || null)}
            className="w-full px-3 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">All Provinces</option>
            {provinces.map((province) => (
              <option key={province.id} value={province.id}>
                {province.name}
              </option>
            ))}
          </select>
        </div>

        {selectedProvince && (
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              District
            </label>
            <select
              value={selectedDistrict || ''}
              onChange={(e) => onDistrictChange(e.target.value || null)}
              className="w-full px-3 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">All Districts</option>
              {districts.map((district) => (
                <option key={district.id} value={district.id}>
                  {district.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-4 space-y-2">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="h-12 bg-muted animate-pulse rounded-md" />
            ))}
          </div>
        ) : (
          <div className="p-2">
            {constituencies.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                No constituencies found
              </div>
            ) : (
              constituencies.map((constituency) => (
                <button
                  key={constituency.id}
                  onClick={() => onConstituencySelect(constituency)}
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-accent transition-colors"
                >
                  <div className="font-medium text-sm text-foreground">
                    {constituency.name}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {constituency.code}
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </aside>
  );
}
